/**
 * Activity Log
 * Filterable activity feed with entity links
 */

import React, { useState } from 'react';
import {
  Activity,
  Search,
  Filter,
  FileText,
  Image,
  Layout,
  Code2,
  FormInput,
  Globe,
  User,
} from 'lucide-react';
import type { ActivityLogEntry } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface ActivityLogProps {
  entries: ActivityLogEntry[];
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  publish: 'bg-purple-100 text-purple-700',
  unpublish: 'bg-yellow-100 text-yellow-700',
  approve: 'bg-green-100 text-green-700',
  reject: 'bg-red-100 text-red-700',
  submit_review: 'bg-orange-100 text-orange-700',
};

const ENTITY_ICONS: Record<string, React.ReactNode> = {
  page: <FileText className="h-4 w-4" />,
  form: <FormInput className="h-4 w-4" />,
  asset: <Image className="h-4 w-4" />,
  content_item: <Layout className="h-4 w-4" />,
  component: <Code2 className="h-4 w-4" />,
  site: <Globe className="h-4 w-4" />,
};

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ActivityLog({ entries, onLoadMore, hasMore }: ActivityLogProps) {
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = entries.filter(entry => {
    const matchesAction = !filterAction || entry.action === filterAction;
    const matchesEntity = !filterEntity || entry.entityType === filterEntity;
    const matchesSearch = !searchQuery ||
      (entry.entityName || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesAction && matchesEntity && matchesSearch;
  });

  const actions = Array.from(new Set(entries.map(e => e.action)));
  const entityTypes = Array.from(new Set(entries.map(e => e.entityType)));

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold">Activity Log</h2>
          <span className="text-sm text-gray-500">{filtered.length} entries</span>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search activities..."
            className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Actions</option>
          {actions.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={filterEntity}
          onChange={(e) => setFilterEntity(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Types</option>
          {entityTypes.map(t => (
            <option key={t} value={t}>{t.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {/* Activity Feed */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Activity className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No activity to display</p>
          </div>
        ) : (
          <div className="divide-y">
            {filtered.map((entry) => (
              <div key={entry.id} className="px-4 py-3 hover:bg-gray-50 flex items-start gap-3">
                {/* Entity Icon */}
                <div className="mt-0.5 p-1.5 bg-gray-100 rounded">
                  {ENTITY_ICONS[entry.entityType] || <FileText className="h-4 w-4" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{entry.userId}</span>
                    {' '}
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${
                      ACTION_COLORS[entry.action] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {entry.action}
                    </span>
                    {' '}
                    <span className="text-gray-600">{entry.entityType.replace('_', ' ')}</span>
                    {entry.entityName && (
                      <>
                        {' '}
                        <span className="font-medium text-blue-600 cursor-pointer hover:underline">
                          {entry.entityName}
                        </span>
                      </>
                    )}
                  </p>

                  {/* Changes */}
                  {entry.changes && Object.keys(entry.changes).length > 0 && (
                    <div className="mt-1">
                      {Object.entries(entry.changes).slice(0, 3).map(([field, change]) => (
                        <p key={field} className="text-xs text-gray-500">
                          <span className="font-mono">{field}</span>: {
                            typeof change === 'object' && change !== null && 'old' in change
                              ? `${change.old} → ${change.new}`
                              : String(change)
                          }
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatTimeAgo(entry.createdAt)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {hasMore && onLoadMore && (
          <div className="p-4 text-center">
            <button
              onClick={onLoadMore}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityLog;
